import { useAuth } from '@/contexts/AuthContext';
import { useMultiTeam } from '@/hooks/useMultiTeam';
import { actsAsLegacyRole, hasWorkRole } from '@/lib/roles/multiRole';

export type AppRole =
  | 'MANAGER'
  | 'SERVICE_WRITER'
  | 'TECHNICIAN'
  | 'DISPATCHER'
  | 'PARTS'
  | 'AUDITOR'
  | 'ORG_ADMIN'
  | 'OWNER'
  // Floo Network Phase 5 — destination roles for region/district
  // managers. Operational permissions for these roles are intentionally
  // NOT granted in this hook in Phase 5 (defer to Phase 7 / region+
  // district dashboards). Phase 5 only enables assignment of the role
  // to a scope so the product language is expressible end-to-end.
  | 'REGION_MANAGER'
  | 'DISTRICT_MANAGER';

export const usePermissions = () => {
  const { membership, activeShop } = useAuth();
  const { isMultiTeamEnabled } = useMultiTeam();

  const role = membership?.role as AppRole | undefined;

  // ── MULTI-ROLE BRIDGE ───────────────────────────────────────────────────
  //
  // This used to be `allowedRoles.includes(membership.role)` over a single
  // scalar. A multi-role member carries the NEUTRAL legacy value STAFF, so
  // that test answered "none of them" and locked a working technician out of
  // the ticket list.
  //
  // `actsAsLegacyRole` projects authority + work roles back onto the old
  // vocabulary, so every predicate below keeps reading the way it always did
  // while the underlying model is the new one. Two projections are absent on
  // purpose: STAFF authority grants nothing by itself, and DETAIL_SPECIALIST
  // maps to NO legacy role — a detailer must never be seen as a TECHNICIAN by
  // code that predates work roles, which is exactly how they would otherwise
  // be handed mechanical work.
  //
  // A legacy-only row (no authority_role, no work_roles loaded) resolves
  // through the same function and behaves exactly as before.
  const hasRole = (...allowedRoles: AppRole[]) =>
    actsAsLegacyRole(membership as never, ...allowedRoles);

  return {
    // ============================================
    // SHOP PROFILE & SETTINGS
    // ============================================
    canEditShopProfile: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewShopProfile: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN', 'AUDITOR'),
    
    // ============================================
    // TICKETS
    // ============================================
    canCreateTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canEditTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canDeleteTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canAssignTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'ORG_ADMIN'),
    // Slice 5D-1: explicit advisor reassignment (custody event_kind
    // `reassign_within_foh`). Mirrors the intersection of the
    // `transition-ticket-custody` edge function role gate (OWNER,
    // MANAGER, ORG_ADMIN, SERVICE_WRITER, TECHNICIAN) and the product
    // intent for *advisor* reassignment — TECHNICIAN is excluded here
    // because explicit advisor reassign is not a tech-side action.
    // DISPATCHER is excluded because the edge function role gate does
    // not allow them.
    canReassignAdvisor: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canViewTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'DISPATCHER', 'PARTS', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // DELETED ITEMS (Soft-deleted ticket recovery)
    // ============================================
    canRestoreTickets: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canPurgeTickets: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewDeletedTickets: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // TECHNICIANS
    // ============================================
    canEditTechnicians: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewTechnicians: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // SHOP FLOOR / BAY MANAGEMENT
    // ============================================
    canEditShopFloor: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewShopFloor: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // TIMERS (Clock in/out, job timing)
    // ============================================
    // A stopwatch belongs to whoever does VEHICLE WORK — plus the
    // administrative authorities that have always operated timers.
    //
    // This used to be `hasRole('TECHNICIAN', …)` alone, and that locked out a
    // legitimate Detail Specialist: multi-role doctrine deliberately refuses to
    // project DETAIL_SPECIALIST onto legacy TECHNICIAN, precisely so code that
    // predates work roles can never hand a detailer mechanical work. The side
    // effect was that a detailer could not Start, Pause, Resume or Stop their
    // OWN detail work, even though the server's assignment model considers them
    // a vehicle-work employee.
    //
    // Fixed through the NEW model rather than by widening the legacy
    // projection: the vehicle-work roles are exactly those with a work family
    // in `WORK_ROLE_FAMILIES`. Mechanical assignment semantics are untouched —
    // eligibility for a given ticket is still decided by work family, on the
    // server, by `technician_assignment_refusal`. This grants the CONTROL
    // SURFACE, never the entitlement.
    canControlTimers:
      hasRole('TECHNICIAN', 'OWNER', 'MANAGER', 'ORG_ADMIN') ||
      hasWorkRole(membership as never, 'DETAIL_SPECIALIST'),

    // ============================================
    // TERMINAL COMPLETION (ending a customer visit)
    // ============================================
    // DELIBERATELY SEPARATE FROM canControlTimers. Running a stopwatch and
    // ending a customer's visit are different capabilities, and the role lists
    // genuinely differ: a SERVICE_WRITER and a DISPATCHER may finish a visit
    // but do not operate timers, while canControlTimers omits both. Nesting
    // Finish Work under timer permission hid it from exactly the two roles
    // that close the most tickets.
    //
    // Mirrors the server predicate `can_complete_tickets_for_shop`. The server
    // remains the authority; this only decides what is worth showing.
    canCompleteTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER',
                                'DISPATCHER', 'TECHNICIAN', 'ORG_ADMIN'),

    // ============================================
    // RELEASING A FINISHED VEHICLE TO THE CUSTOMER
    // ============================================
    // 8G.2C.7. DELIBERATELY ITS OWN CAPABILITY, not a borrowed one.
    //
    // The Optiboard button rendered unconditionally and the Birdseye sheet was
    // gated on `canAssignTickets`, whose role set happens to match today. That
    // coincidence is not a reason: assigning work and handing a car to a
    // customer are different capabilities, and if either list moves the other
    // must not silently follow.
    //
    // Mirrors the server law in `mark_completed_ticket_picked_up` exactly:
    // everyone who may FINISH a visit except TECHNICIAN, because releasing a
    // vehicle is front-of-house. The server remains authoritative; this only
    // decides what is worth showing.
    //
    // NOT the same as Picked Up Incomplete, which keeps ticket-edit law and
    // excludes DISPATCHER — blocking a live visit is not front-of-house work.
    canReleaseCompletedVehicle: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER',
                                        'DISPATCHER', 'ORG_ADMIN'),
    // Oct 20 FD-01 + addendum L1: who may physically move a vehicle as a standalone act. Mirrors the server
    // predicate `can_relocate_vehicles_for_shop` (front office + TECHNICIAN + DETAIL_SPECIALIST, resolved
    // multi-role). The Detail Specialist is read through the work role — NOT through `hasRole` — so relocation
    // never projects a detailer onto TECHNICIAN (timer / mechanical permissions are unchanged). The server remains
    // authoritative; this only decides what is worth showing.
    canRelocateVehicles: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'ORG_ADMIN', 'TECHNICIAN')
      || hasWorkRole(membership as never, 'DETAIL_SPECIALIST')
      || hasWorkRole(membership as never, 'DISPATCHER'),
    
    // ============================================
    // OPTIMIZER & SMART ASSIGN
    // ============================================
    canRunOptimizer: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'ORG_ADMIN'),
    
    // ============================================
    // IMPORT/EXPORT
    // ============================================
    canImportCSV: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canImportCatalog: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canExportData: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'), // Auditors cannot export
    
    // ============================================
    // REPORTS & ANALYTICS
    // ============================================
    canViewBirdseye: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'AUDITOR', 'ORG_ADMIN', 'TECHNICIAN'),
    canViewOverview: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // VEHICLE SYNC
    // ============================================
    canSyncVehicleData: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // REMINDER SETTINGS
    // ============================================
    canConfigureReminders: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewReminderLogs: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // BILLING
    // ============================================
    canManageBilling: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewBilling: hasRole('OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'),
    canViewInvoices: hasRole('OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'),

    // ============================================
    // EXTERNAL INTEGRATIONS (Batch 17-A)
    // Connect / disconnect / test / rotate-key for shop_integrations
    // rows. Mirrors canManageBilling — same admin-only surface set.
    // Used to gate the External Integrations tab in ShopProfile.
    // ============================================
    canManageExternalIntegrations: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewExternalIntegrations: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN', 'AUDITOR'),

    // ============================================
    // CUSTOMER INVOICING (Phase 11)
    // ============================================
    canViewCustomerInvoices: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canManageCustomerInvoices: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canVoidCustomerInvoice: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canIssueCustomerInvoiceRefund: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    // STAGE 0C — the manager WORK override of pre-repair authorization. Exactly
    // the server's authority (`start_ticket_with_preauth_override`: has_role
    // OWNER or MANAGER). It is deliberately NOT canVoidCustomerInvoice, which
    // also includes ORG_ADMIN — that offered ORG_ADMIN a button the server
    // always refused. A multi-role owner/manager who is also a technician holds
    // it and may override their own work.
    canOverridePreauth: hasRole('OWNER', 'MANAGER'),

    // ============================================
    // TIME CLOCK
    // ============================================
    canViewTimeClockReports: hasRole('OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'),
    canEditTimeClockEntries: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // PAYROLL REPORTS
    // ============================================
    canViewPayrollReports: hasRole('OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'),
    canExportPayroll: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // DIAGNOSTIC OUTCOMES
    // ============================================
    canRecordDiagnosticOutcome: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    
    // ============================================
    // OPTIDVI (Inspection template editor)
    // ============================================
    canEditInspectionTemplates: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewInspectionTemplates: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // OPTIASSETS (Asset tracking addon)
    // ============================================
    canViewAssets: hasRole('OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'),
    canEditAssets: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canDeleteAssets: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canLogAssetUsage: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canViewAssetCosts: hasRole('OWNER', 'MANAGER', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // PARTS MANAGEMENT
    // ============================================
    canViewParts: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'PARTS', 'AUDITOR', 'ORG_ADMIN'),
    canEditParts: hasRole('OWNER', 'MANAGER', 'PARTS', 'ORG_ADMIN'),
    canOrderParts: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'PARTS', 'ORG_ADMIN'),
    
    // ============================================
    // MULTI-TEAM (Enterprise feature)
    // ============================================
    canManageTeams: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN') && isMultiTeamEnabled,
    canViewAllTeams: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canReassignTicketTeam: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canOverrideTeamIsolation: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    isMultiTeamEnabled,
    
    // ============================================
    // USER MANAGEMENT (ORG_ADMIN exclusive)
    // ============================================
    canInviteUsers: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canRemoveUsers: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    canChangeUserRoles: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // DISPATCH BOARD (DISPATCHER specific)
    // ============================================
    canViewDispatchBoard: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'ORG_ADMIN'),
    canDragDropTickets: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'DISPATCHER', 'ORG_ADMIN'),
    
    // ============================================
    // OPTIBAY TRANSPARENCY (Read-only visibility)
    // ============================================
    canViewAllTechColumns: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'DISPATCHER', 'AUDITOR', 'ORG_ADMIN'),
    // Core-100 R4 (CORE-AB/AE, browser proof): the ranking evaluations are front-office only at the server
    // (`assignment_audit` SELECT = is_manager_or_writer, R4-5). The tab used to be offered to technicians,
    // dispatchers and auditors too, where it could only render an empty ranking — absence presented as data.
    canViewRankingTab: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    canViewTicketPII: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    // OCT28 #1 (founder decision 2026-10-29): the vehicle's identity — plate, issuing state, VIN — is shown to the
    // technicians who work the car as well. ONLY that: customer contact keeps OD-34 and every other use of
    // canViewTicketPII is unchanged. Display only; tickets / vehicles rows remain governed by RLS.
    canViewVehicleIdentity: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN', 'TECHNICIAN'),
    // OD-34 (founder ruling 2026-10-13): technicians and detail specialists of the shop READ customer contact
    // (name, phone, email, address — any ticket, not only their own). The server is the authority
    // (`can_read_shop_customer_contact`, migration 20270206025400; tickets rows via RLS); this only decides what to
    // show. READING IS NOT EDITING: every contact / consent / Due Out edit control stays on `canEditTickets`.
    // (`canViewTicketPII` above still governs VIN / plate display and is not part of OD-34.)
    // OD-34a (founder ruling 2026-10-14): EVERY active member of the shop reads contact, whatever the role (legacy
    // dispatcher / parts / auditor / bare staff included; the server predicate admits any active membership of the
    // shop). `membership` is the active membership of the active shop (AuthContext loads status = 'active' only).
    // This opens no new workflow: routes and navigation are unchanged.
    canViewCustomerContact:
      !!membership || hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'ORG_ADMIN'),
    
    // ============================================
    // DIAGNOSTIC LOAD GUARD (DLG)
    // ============================================
    canManageDlgConfig: hasRole('OWNER', 'MANAGER', 'ORG_ADMIN'),
    
    // ============================================
    // WORKFLOW REPORT
    // ============================================
    canEditWorkflowComplaints: hasRole('SERVICE_WRITER', 'OWNER', 'MANAGER', 'ORG_ADMIN'),
    canEditWorkflowInspections: hasRole('TECHNICIAN', 'OWNER', 'MANAGER', 'ORG_ADMIN'),
    canEditWorkflowDiagnostics: hasRole('TECHNICIAN', 'OWNER', 'MANAGER', 'ORG_ADMIN'),
    canEditWorkflowNotes: hasRole('OWNER', 'MANAGER', 'SERVICE_WRITER', 'TECHNICIAN', 'DISPATCHER', 'PARTS', 'AUDITOR', 'ORG_ADMIN'),
    
    // ============================================
    // ROLE IDENTIFIERS
    // ============================================
    //
    // These went through `hasRole` above but were left comparing the raw
    // scalar, which quietly made them the ONE place in this hook that still
    // believed a person has exactly one role. A manager who also turns
    // wrenches carries `role = 'MANAGER'`, so `isTechnician` said no — and
    // every surface that gates a technician control on it hid that control
    // from somebody actually doing the job.
    //
    // `actsAsLegacyRole` is the same projection every predicate above uses,
    // so these now answer the question they have always CLAIMED to answer:
    // "does this person act as a technician here?", not "is this the string
    // in their legacy column?".
    //
    // `role` itself stays exactly as it was. It is the legacy scalar, a great
    // many things read it, and silently changing what it contains would be a
    // far larger and less visible change than fixing the booleans that were
    // supposed to insulate callers from it in the first place.
    role,
    isOwner: hasRole('OWNER'),
    isManager: hasRole('MANAGER'),
    isServiceWriter: hasRole('SERVICE_WRITER'),
    isTechnician: hasRole('TECHNICIAN'),
    isDispatcher: hasRole('DISPATCHER'),
    isParts: hasRole('PARTS'),
    isAuditor: hasRole('AUDITOR'),
    isOrgAdmin: hasRole('ORG_ADMIN'),
    // Phase 5 — identifiers for the new scope-aware management roles.
    // No operational permissions are granted yet; the booleans exist so
    // future phases can wire scope-specific dashboards without re-touching
    // the role identifier surface.
    //
    // These two stay on the SCALAR, deliberately. Floo Network Phase 5 grants
    // them no operational permission, and `p5FlooNetworkPeopleScope` enforces
    // that by asserting no `hasRole(` line in this file names either role —
    // a guard worth keeping intact. It costs nothing here: REGION_MANAGER and
    // DISTRICT_MANAGER are AUTHORITY, and `legacyScalarFor` writes the
    // authority into the scalar whenever it is not STAFF, so the scalar is not
    // lossy for them the way it is for a work role.
    isRegionManager: role === 'REGION_MANAGER',
    isDistrictManager: role === 'DISTRICT_MANAGER',
    /**
     * Whether this person performs DETAIL work — and nothing about whether
     * they are a technician.
     *
     * Deliberately NOT expressed through `hasRole`: DETAIL_SPECIALIST has no
     * legacy projection, because a detailer seen as a TECHNICIAN by code that
     * predates work roles is exactly how they end up handed mechanical work.
     * Surfaces that need to know read this instead.
     */
    isDetailSpecialist: hasWorkRole(membership as never, 'DETAIL_SPECIALIST'),

    // Helper for checking multiple roles
    hasRole,
  };
};
